import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { verifyOtpRequest, resendOtpRequest, resetVerifyOtpState } from '../../redux/slices/authSlice';
import snapmenuLogo from '../../assets/snapmenu.png';

export const OtpVerification: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const { 
    pendingUser,
    verifyOtp: { loading: verifyLoading, success: verifySuccess, error: verifyError, verifyData },
    resendOtp: { loading: resendLoading, success: resendSuccess }
  } = useAppSelector(state => state.auth);
  const { user } = useAppSelector(state => state.auth);
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [otpTimer, setOtpTimer] = useState(120); // 2 minutes

  // Redirect to login if no pending user in Redux
  useEffect(() => {
    if (!pendingUser) {
      navigate('/login', { replace: true });
    }
  }, [pendingUser, navigate]);

  // Get email from localStorage (survives refresh) with fallback to Redux pendingUser
  const getPendingUserEmail = () => {
    try {
      const storedUser = localStorage.getItem('pendingUser');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        return parsedUser.email || '';
      }
    } catch (error) {
      console.error('Error reading pendingUser from localStorage:', error);
    }
    // Fallback to Redux
    return pendingUser?.email || '';
  };

  const email = getPendingUserEmail();

  // OTP Timer countdown
  useEffect(() => {
    if (otpTimer > 0) {
      const timerId = setInterval(() => {
        setOtpTimer(prev => prev - 1);
      }, 1000);
      
      return () => clearInterval(timerId);
    }
  }, [otpTimer]);

  // Handle OTP verification success
  useEffect(() => {
    if (verifySuccess && user) {
      setTimeout(() => {
        dispatch(resetVerifyOtpState());
        // Navigate based on user role (case-insensitive comparison)
        if (user?.role?.toLowerCase() === 'superadmin') {
          navigate('/companies', { replace: true });
        } else {
          navigate('/api-reference/introduction', { replace: true });
        }
      }, 1500);
    }
  }, [verifySuccess, user, navigate, dispatch]);

  // Clear OTP inputs when verification fails
  useEffect(() => {
    if (verifyError) {
      setOtp(['', '', '', '', '', '']);
      // Focus on first input
      const firstInput = document.getElementById('otp-0');
      firstInput?.focus();
    }
  }, [verifyError]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      
      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    
    // Check if pasted data is exactly 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      
      // Focus on the last input
      const lastInput = document.getElementById('otp-5');
      lastInput?.focus();
    }
  };

  const handleVerifyOtp = () => {
    const otpValue = otp.join('');
    
    if (otpValue.length === 6 && email) {
      dispatch(verifyOtpRequest({ email, code: otpValue }));
    }
  };

  const handleResendOtp = () => {
    if (email) {
      dispatch(resendOtpRequest({ email }));
      // Reset timer
      setOtpTimer(120);
      // Clear OTP inputs
      setOtp(['', '', '', '', '', '']);
    }
  };

  const formatTimer = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* OTP Verification Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden relative">
          {/* Timer in top right corner - only show when > 0 */}
          {otpTimer > 0 && (
            <div className="absolute top-4 right-4 bg-gradient-to-r from-red-500 to-orange-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-bold text-sm">{formatTimer(otpTimer)}</span>
            </div>
          )}

          {/* Logo/Header Section */}
          <div className="px-8 py-8 text-center">
            <div className="flex justify-center mb-6">
              <img 
                src={snapmenuLogo} 
                alt="SnapMenuAI Logo" 
                className="h-16 object-contain"
              />
            </div>
            <div className="bg-gradient-to-r from-red-500 to-orange-600 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center shadow-lg">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Enter Verification Code</h2>
            <p className="text-gray-600 text-sm mb-3">
              An OTP has been sent to your email
            </p>
            {email && (
              <div className="bg-gradient-to-r from-red-500/10 to-orange-600/10 border-2 border-red-500/30 rounded-xl p-3 mb-4">
                <p className="text-red-500 font-bold text-lg break-all">
                  {email}
                </p>
              </div>
            )}
          </div>

          {/* Form Section */}
          <div className="px-8 pb-8">
            <div className="mb-6">
              {/* Resend Success Message */}
              {resendSuccess && (
                <div className="bg-green-50 border-2 border-green-500 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-green-700 font-bold text-sm">
                      OTP resent successfully! Please check your email.
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-gradient-to-r from-red-500 to-orange-600 text-white text-sm font-bold py-2 px-4 rounded-lg mb-4 text-center">
                Enter 6-Digit OTP
              </div>

              {/* OTP Inputs */}
              <div className="flex gap-2 justify-center mb-3">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={handleOtpPaste}
                    disabled={verifyLoading}
                    className={`w-12 h-12 text-center text-xl font-bold border-2 rounded-lg focus:outline-none transition-all ${
                      verifyError 
                        ? 'border-red-500 focus:border-red-600 focus:ring-2 focus:ring-red-200' 
                        : 'border-gray-300 focus:border-red-500 focus:ring-2 focus:ring-orange-200'
                    }`}
                  />
                ))}
              </div>

              {/* Error Message */}
              {verifyError && (
                <div className="bg-red-50 border-2 border-red-500 rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-700 font-bold text-sm">
                      OTP verification failed. Try again.
                    </p>
                  </div>
                </div>
              )}

              {/* Success Message */}
              {verifySuccess && (
                <div className="bg-green-50 border-2 border-green-500 rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-green-700 font-bold text-sm">
                      OTP verified successfully! Redirecting...
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Resend Button */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1"></div>
              <button
                onClick={handleResendOtp}
                disabled={resendLoading || otpTimer > 0}
                className="text-sm text-red-500 hover:text-orange-700 font-semibold underline disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {resendLoading ? 'Resending...' : otpTimer > 0 ? `Resend OTP (${formatTimer(otpTimer)})` : 'Resend OTP'}
              </button>
            </div>

            {/* Verify Button */}
            <button
              onClick={handleVerifyOtp}
              disabled={otp.join('').length !== 6 || verifyLoading}
              className="w-full bg-gradient-to-r from-red-500 to-orange-600 text-white font-bold py-3 px-6 rounded-xl hover:from-red-600 hover:to-orange-700 transform hover:scale-[1.02] transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {verifyLoading ? 'Verifying...' : 'Verify OTP'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
